import React, { useEffect, useMemo, useState } from 'react';
import { analyticsAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Analytics.css';
import { formatCurrencyGBP } from '../utils/currency';

const Analytics = ({ data }) => {
  // Local analytics state (overrides parent when month is selected)
  const [analyticsData, setAnalyticsData] = useState(data || { overview: {}, topCustomers: [], ordersByDay: [] });
  const { overview, topCustomers, ordersByDay } = analyticsData;

  // Normalize ordersByDay to ensure chart compatibility
  const normalizedOrders = useMemo(() => (
    Array.isArray(ordersByDay)
    ? ordersByDay.map((item) => {
        const rawDate = item.date || item.order_date || item.day || item.label || '';
        let date = '';
        try {
          if (rawDate) {
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) {
              date = d.toISOString().slice(0, 10); // YYYY-MM-DD
            } else if (typeof rawDate === 'string') {
              // fallback to raw string if already a date label
              date = rawDate;
            }
          }
        } catch (_) {
          date = typeof rawDate === 'string' ? rawDate : '';
        }
        // Coerce counts and revenue to numbers and handle alternate keys
        const orderCount =
          typeof item.order_count !== 'undefined'
            ? Number(item.order_count)
            : typeof item.orders !== 'undefined'
            ? Number(item.orders)
            : typeof item.count !== 'undefined'
            ? Number(item.count)
            : 0;
        const revenue =
          typeof item.revenue !== 'undefined'
            ? Number(item.revenue)
            : typeof item.totalRevenue !== 'undefined'
            ? Number(item.totalRevenue)
            : typeof item.amount !== 'undefined'
            ? Number(item.amount)
            : 0;
        return { date, order_count: orderCount, revenue };
      })
    : []
  ), [ordersByDay]);

    // Fetch month options from backend (distinct months with orders)
    const [monthOptions, setMonthOptions] = useState([]);
    useEffect(() => {
      let mounted = true;
      analyticsAPI.getMonths()
        .then((res) => {
          if (mounted) {
            const months = Array.isArray(res.data.months) ? res.data.months : [];
            setMonthOptions(months);
          }
        })
        .catch(() => {
          // Fallback: derive from normalizedOrders if API fails
          const set = new Set();
          normalizedOrders.forEach((o) => {
            if (o.date && o.date.length >= 7) set.add(o.date.substring(0, 7));
          });
          setMonthOptions(Array.from(set).sort((a, b) => (a < b ? 1 : -1)));
        });
      return () => { mounted = false; };
    }, [normalizedOrders]);

    // Selected month state defaults to latest available
    const [selectedMonth, setSelectedMonth] = useState('');

    useEffect(() => {
      if (monthOptions.length > 0) {
        setSelectedMonth((prev) => prev || monthOptions[0]);
      } else {
        setSelectedMonth('');
      }
    }, [monthOptions]);

    // Filter orders by selected month
    const monthFilteredOrders = useMemo(() => {
      if (!selectedMonth) return normalizedOrders;
      return normalizedOrders.filter((o) => o.date && o.date.startsWith(selectedMonth));
    }, [normalizedOrders, selectedMonth]);

    // Compute month-specific overview when a month is selected (fallback to given overview)
    const computedOverview = useMemo(() => {
      return overview;
    }, [overview]);

    // Refetch analytics when selected month changes
    useEffect(() => {
      const fetchMonth = async () => {
        try {
          const params = selectedMonth ? { month: selectedMonth } : {};
          const res = await analyticsAPI.getOverview(params);
          if (res && res.data) {
            setAnalyticsData(res.data);
          }
        } catch (e) {
          // keep existing data on error
        }
      };
      fetchMonth();
    }, [selectedMonth]);

  // Responsive chart height based on viewport width
  const [chartHeight, setChartHeight] = useState(420);

  useEffect(() => {
    const computeHeight = () => {
      const w = window.innerWidth;
      if (w < 640) return 260; // phones
      if (w < 1024) return 340; // tablets / small laptops
      if (w < 1440) return 420; // desktops
      return 520; // large / ultrawide
    };
    const applyHeight = () => setChartHeight(computeHeight());
    applyHeight();
    window.addEventListener('resize', applyHeight);
    return () => window.removeEventListener('resize', applyHeight);
  }, []);

  const formatCurrency = (value) => formatCurrencyGBP(value);

  return (
    <div className="analytics">
      {/* Top Month Selector aligned left */}
      <div className="analytics-topbar">
        <div className="selector-card inline">
          <div className="month-selector">
            <label htmlFor="globalMonthSelect">Month:</label>
            <select
              id="globalMonthSelect"
              className="input-field"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All</option>
              {monthOptions.length > 0 ? (
                monthOptions.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))
              ) : (
                <option value="" disabled>No months detected</option>
              )}
            </select>
            <small className="selector-hint">
              {selectedMonth ? `Viewing ${selectedMonth}` : 'Viewing all months'}
            </small>
          </div>
        </div>
      </div>
      {/* Overview Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Customers</h3>
          <p className="stat-value">{overview.totalCustomers}</p>
        </div>
        <div className="stat-card">
          <h3>Total Orders</h3>
          <p className="stat-value">{Math.round(computedOverview.totalOrders || 0)}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-value">{formatCurrency(computedOverview.totalRevenue || 0)}</p>
        </div>
        <div className="stat-card">
          <h3>Avg Order Value</h3>
          <p className="stat-value">{formatCurrency(computedOverview.averageOrderValue || 0)}</p>
        </div>
        <div className="stat-card highlight">
          <h3>Orders This Month</h3>
          <p className="stat-value">{Math.round(computedOverview.ordersThisMonth || 0)}</p>
        </div>
        <div className="stat-card highlight">
          <h3>Revenue This Month</h3>
          <p className="stat-value">{formatCurrency(computedOverview.revenueThisMonth || 0)}</p>
        </div>
      </div>

      {/* Charts - Full width stacked */}
      <div className="charts-grid">
        <div className="card chart-card">
          <div className="chart-header">
            <h3>Orders & Revenue</h3>
            <span className="selected-month-badge">
              {selectedMonth ? `Viewing ${selectedMonth}` : 'Viewing All Months'}
            </span>
          </div>
          {monthFilteredOrders && monthFilteredOrders.length > 0 ? (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={monthFilteredOrders}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="order_count"
                  stroke="#2196f3"
                  name="Orders"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="revenue"
                  stroke="#4caf50"
                  name="Revenue (£)"
                />
                <Tooltip formatter={(val, name, props) => {
                  if (props && (props.dataKey === 'revenue')) {
                    return [formatCurrency(val), name];
                  }
                  // Orders series
                  const intVal = Number(val);
                  return [Number.isFinite(intVal) ? Math.round(intVal) : val, name];
                }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-message">No orders or revenue data for the selected period.</div>
          )}
        </div>

        <div className="card chart-card">
          <h3>Top 10 Customers by Spending</h3>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={topCustomers}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(val, name, props) => {
                if (props && (props.dataKey === 'total_spent')) {
                  return [formatCurrency(val), name];
                }
                const intVal = Number(val);
                return [Number.isFinite(intVal) ? Math.round(intVal) : val, name];
              }} />
              <Legend />
              <Bar dataKey="total_spent" fill="#d32f2f" name="Total Spent (£)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* High-Value Customers table removed (feature now on Customers tab) */}
    </div>
  );
};

export default Analytics;
